/**
 * Schema-typed JSON writer used by every encoder (RPE chart, extra.json, sidecar; W1-07a formats-json-rpe,
 * contract formats/json/types.ts `JsonWriter`).
 *
 * Typing rules (rpe-format.md F§0.2, F§15; DESIGN.md D§7.3):
 *  - `int` asserts an integer inside the range of its Rust type (i32 / u8 / u16 / u32) and writes it without a
 *    fraction; -0 is written as 0;
 *  - `f32` writes the shortest decimal that reads back (via f64, as serde_json does) as Math.fround(v); `f64` the
 *    shortest round-trip decimal; in the 'rpe' float style integral floats get ".0" ("255.0") as RPE writes them;
 *  - non-finite numbers are refused everywhere (EncodeError: a bug, caught by the export self-check);
 *  - `rawNumber` writes a preserved number lexeme verbatim (e.g. RPEVersion `170.0`) after checking the JSON
 *    number grammar; `rawString` does the same for a preserved string token
 *    (RPEVersion `"\u0031\u0037\u0030"` stays exactly as written);
 *  - `json` writes a passthrough value verbatim (integral numbers as integers, others in shortest form);
 *  - `finish` returns UTF-8 without a BOM (TextEncoder; lone surrogates become U+FFFD).
 *
 * Structure is checked as well: a key only inside an object and before each member's value, values inside an
 * object only after a key, exactly one root value, every container closed. Errors carry the JSON path of the
 * offending value (e.g. "judgeLineList[3].notes[12].above").
 *
 * Layout: minified, or `pretty` with two-space indentation where short arrays of scalars (beat triples, colours,
 * bezier points) stay on one line: `"startTime": [4, 0, 1]`.
 */

import type { Json } from '../../core/util/types';
import { EncodeError } from './types';
import type { CreateJsonWriterFn, JsonWriter } from './types';
import { floatLexeme, JSON_NUMBER_RE, shortestF64 } from './shortest';

/** True when `lexeme` is exactly one JSON string token (RFC 8259: no raw control characters, valid escapes). */
export function isJsonStringToken(lexeme: string): boolean {
  if (lexeme.length < 2 || !lexeme.startsWith('"') || !lexeme.endsWith('"')) return false;
  for (let i = 1; i < lexeme.length - 1; i++) {
    const c = lexeme.charCodeAt(i);
    if (c < 0x20 || c === 0x22) return false;
    if (c !== 0x5c) continue;
    const e = lexeme[i + 1] ?? '';
    if (i + 1 >= lexeme.length - 1) return false;
    if ('"\\/bfnrt'.includes(e)) {
      i++;
      continue;
    }
    if (e !== 'u' || !/^[0-9a-fA-F]{4}$/.test(lexeme.slice(i + 2, i + 6)) || i + 6 > lexeme.length - 1) return false;
    i += 5;
  }
  return true;
}

const INT_RANGES = {
  i32: [-2147483648, 2147483647],
  u8: [0, 255],
  u16: [0, 65535],
  u32: [0, 4294967295],
} as const;

/** Longest single-line rendering of a scalar array in pretty mode. */
const INLINE_ARRAY_MAX = 72;

interface Frame {
  readonly kind: 'obj' | 'arr';
  /** Rendered members ("\"key\":value" for objects, values for arrays). */
  readonly items: string[];
  /** True while every member is a scalar (arrays only: decides inline layout). */
  scalarsOnly: boolean;
  /** Key waiting for its value (objects). */
  pendingKey: string | null;
  /** Path of this container. */
  readonly path: string;
}

function escapeCode(c: number): string {
  switch (c) {
    case 0x22:
      return '\\"';
    case 0x5c:
      return '\\\\';
    case 0x08:
      return '\\b';
    case 0x0c:
      return '\\f';
    case 0x0a:
      return '\\n';
    case 0x0d:
      return '\\r';
    case 0x09:
      return '\\t';
    default:
      return `\\u${c.toString(16).padStart(4, '0')}`;
  }
}

/** JSON string literal (quotes, backslashes and control characters escaped; everything else verbatim). */
export function quoteJsonString(s: string): string {
  let out = '"';
  let chunk = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c === 0x22 || c === 0x5c) {
      out += s.slice(chunk, i) + escapeCode(c);
      chunk = i + 1;
    }
  }
  return `${out}${s.slice(chunk)}"`;
}

function keyPath(parent: string, key: string): string {
  return /^[A-Za-z_$][\w$]*$/.test(key)
    ? parent === ''
      ? key
      : `${parent}.${key}`
    : `${parent}[${quoteJsonString(key)}]`;
}

class Writer implements JsonWriter {
  private readonly pretty: boolean;
  private readonly floatStyle: 'rpe' | 'shortest';
  private readonly stack: Frame[] = [];
  private root: string | null = null;
  private finished = false;

  constructor(pretty: boolean, floatStyle: 'rpe' | 'shortest') {
    this.pretty = pretty;
    this.floatStyle = floatStyle;
  }

  /** Path of the value about to be written. */
  private nextPath(): string {
    const top = this.stack[this.stack.length - 1];
    if (top === undefined) return '';
    if (top.kind === 'obj') return keyPath(top.path, top.pendingKey ?? '?');
    return `${top.path}[${String(top.items.length)}]`;
  }

  private fail(message: string, path: string = this.nextPath()): never {
    throw new EncodeError(path === '' ? '(root)' : path, message);
  }

  private checkValueAllowed(): void {
    if (this.finished) this.fail('the writer is already finished');
    const top = this.stack[this.stack.length - 1];
    if (top === undefined) {
      if (this.root !== null) this.fail('a JSON document has exactly one root value');
      return;
    }
    if (top.kind === 'obj' && top.pendingKey === null) this.fail('a value inside an object needs a key first');
  }

  /** Appends a rendered value to the current container (or sets the root). */
  private emit(rendered: string, scalar: boolean): void {
    const top = this.stack[this.stack.length - 1];
    if (top === undefined) {
      this.root = rendered;
      return;
    }
    if (top.kind === 'obj') {
      const key = top.pendingKey ?? '';
      top.items.push(`${quoteJsonString(key)}${this.pretty ? ': ' : ':'}${rendered}`);
      top.pendingKey = null;
    } else {
      top.items.push(rendered);
      if (!scalar) top.scalarsOnly = false;
    }
  }

  private scalar(rendered: string): void {
    this.checkValueAllowed();
    this.emit(rendered, true);
  }

  beginObject(): void {
    this.checkValueAllowed();
    this.stack.push({ kind: 'obj', items: [], scalarsOnly: false, pendingKey: null, path: this.nextPath() });
  }

  endObject(): void {
    const top = this.stack[this.stack.length - 1];
    if (top?.kind !== 'obj') this.fail('endObject without a matching beginObject');
    if (top.pendingKey !== null) this.fail(`key "${top.pendingKey}" has no value`, keyPath(top.path, top.pendingKey));
    this.stack.pop();
    this.emit(this.render(top, '{', '}'), false);
  }

  beginArray(): void {
    this.checkValueAllowed();
    this.stack.push({ kind: 'arr', items: [], scalarsOnly: true, pendingKey: null, path: this.nextPath() });
  }

  endArray(): void {
    const top = this.stack[this.stack.length - 1];
    if (top?.kind !== 'arr') this.fail('endArray without a matching beginArray');
    this.stack.pop();
    this.emit(this.render(top, '[', ']'), false);
  }

  private render(frame: Frame, open: string, close: string): string {
    if (frame.items.length === 0) return `${open}${close}`;
    if (!this.pretty) return `${open}${frame.items.join(',')}${close}`;
    if (frame.kind === 'arr' && frame.scalarsOnly) {
      const inline = `${open}${frame.items.join(', ')}${close}`;
      if (inline.length <= INLINE_ARRAY_MAX) return inline;
    }
    const depth = this.stack.length;
    const inner = '  '.repeat(depth + 1);
    const outer = '  '.repeat(depth);
    return `${open}\n${inner}${frame.items.join(`,\n${inner}`)}\n${outer}${close}`;
  }

  key(k: string): void {
    if (this.finished) this.fail('the writer is already finished');
    const top = this.stack[this.stack.length - 1];
    if (top?.kind !== 'obj') this.fail(`key "${k}" outside an object`);
    if (top.pendingKey !== null) this.fail(`key "${top.pendingKey}" has no value`, keyPath(top.path, top.pendingKey));
    top.pendingKey = k;
  }

  int(v: number, kind: 'i32' | 'u8' | 'u16' | 'u32'): void {
    if (!Number.isInteger(v)) this.fail(`${kind} field needs an integer, got ${String(v)}`);
    const [lo, hi] = INT_RANGES[kind];
    if (v < lo || v > hi) this.fail(`${kind} field out of range [${String(lo)}, ${String(hi)}]: ${String(v)}`);
    this.scalar(v === 0 ? '0' : String(v));
  }

  f32(v: number): void {
    if (!Number.isFinite(Math.fround(v))) this.fail(`f32 field needs a finite value, got ${String(v)}`);
    this.scalar(floatLexeme(v, 'f32', this.floatStyle));
  }

  f64(v: number): void {
    if (!Number.isFinite(v)) this.fail(`f64 field needs a finite value, got ${String(v)}`);
    this.scalar(floatLexeme(v, 'f64', this.floatStyle));
  }

  bool(v: boolean): void {
    this.scalar(v ? 'true' : 'false');
  }

  string(v: string): void {
    this.scalar(quoteJsonString(v));
  }

  null(): void {
    this.scalar('null');
  }

  json(v: Json): void {
    if (v === null) {
      this.null();
      return;
    }
    switch (typeof v) {
      case 'boolean':
        this.bool(v);
        return;
      case 'string':
        this.string(v);
        return;
      case 'number':
        if (!Number.isFinite(v)) this.fail(`passthrough number is not finite (${String(v)})`);
        this.scalar(shortestF64(v));
        return;
      case 'object':
        break;
    }
    if (Array.isArray(v)) {
      this.beginArray();
      for (const item of v as readonly Json[]) this.json(item);
      this.endArray();
      return;
    }
    this.beginObject();
    for (const [k, item] of Object.entries(v as { readonly [k: string]: Json })) {
      this.key(k);
      this.json(item);
    }
    this.endObject();
  }

  rawNumber(lexeme: string): void {
    if (!JSON_NUMBER_RE.test(lexeme)) this.fail(`"${lexeme}" is not a JSON number`);
    this.scalar(lexeme);
  }

  rawString(lexeme: string): void {
    if (!isJsonStringToken(lexeme)) this.fail(`${lexeme} is not a JSON string token`);
    this.scalar(lexeme);
  }

  /** The finished document as text (used by fragment encoders). */
  finishText(): string {
    if (this.finished) this.fail('the writer is already finished');
    const top = this.stack[this.stack.length - 1];
    if (top !== undefined) this.fail(`unclosed ${top.kind === 'obj' ? 'object' : 'array'}`, top.path);
    if (this.root === null) this.fail('nothing was written');
    this.finished = true;
    return this.root;
  }

  finish(): Uint8Array {
    return new TextEncoder().encode(this.finishText());
  }
}

/** A JsonWriter that can also return its document as a string (fragments, tests). */
export interface TextJsonWriter extends JsonWriter {
  finishText(): string;
}

export const createJsonWriter: CreateJsonWriterFn = (opts) => new Writer(opts.pretty, opts.floatStyle);

/** Same writer, typed with `finishText` (formats-internal helper). */
export function createTextJsonWriter(opts: {
  readonly pretty: boolean;
  readonly floatStyle: 'rpe' | 'shortest';
}): TextJsonWriter {
  return new Writer(opts.pretty, opts.floatStyle);
}
